import * as types from './constants';
import endpoints from '../../constants/endpoints';
import { getHttpInstance } from '../../services/defaultHttpService';
import { getStoreInfo } from '../../services/storeInfoService';
import { replaceTokensInUrl, isEmpty } from '@app/utils';
import { setError } from '../exception/actions';
import { normalizeError } from '../exception/utils';
import { ERROR_DOMAIN, ERROR_PATH } from '../../constants/errorConstants';

export const getAddresses = () => async dispatch => {
  dispatch({ type: types.ADDRESSES_GET_BEGIN });
  const { storeId } = getStoreInfo();
  const http = getHttpInstance();
  const url = replaceTokensInUrl(endpoints.ADDRESS_SAVE, storeId);
  try {
    const { data } = await http.get(url);
    dispatch({
      type: types.ADDRESSES_GET_SUCCESS,
      payload: data?.contact ?? [] //if there are no addresses the API will return no 'contact' key
    });
  } catch (error) {
    dispatch({
      type: types.ADDRESSES_GET_FAIL,
      payload: error
    });
    dispatch(
      setError(
        ERROR_DOMAIN.ADDRESS_BOOK,
        ERROR_PATH.MAIN,
        normalizeError(error)
      )
    );
  }
};

export const verifyAddress = ({
  address1 = '',
  address2 = '',
  city = '',
  country = '',
  state = '',
  zipCode = '', //string
  onSuccess = null,
  onFail = null,
  ...rest
}) => {
  const http = getHttpInstance();
  const { storeId } = getStoreInfo();
  const url = replaceTokensInUrl(endpoints.ADDRESS_VERIFICATION, storeId);
  return async dispatch => {
    const newAddress = {
      //named as required by the AVI Service
      address1,
      address2,
      city,
      state,
      country,
      zipCode
    };
    dispatch({
      type: types.ADDRESS_VERIFICATION_BEGIN,
      payload: {
        ...newAddress,
        ...rest
      }
    });
    try {
      const { data } = await http.post(url, {
        ...newAddress,
        requestType: 'create'
      });
      const { aviCheckReq, selection = '', formattedAddress = {} } = data;
      const {
        formattedAddress2,
        primaryPostalCode,
        city: formattedCity,
        state: formattedState,
        country: formattedCountry
      } = formattedAddress || {};
      if (onSuccess) {
        onSuccess();
      }
      dispatch({
        type: types.ADDRESS_VERIFICATION_SUCCESS,
        payload: {
          enabled: aviCheckReq === true || aviCheckReq === 'true', //flag to show modal
          required: selection === 'required',
          address1: formattedAddress2, // (copied from checkout)
          address2: '',
          zipCode: primaryPostalCode,
          city: formattedCity,
          state: formattedState,
          country: formattedCountry,
          aviSelection: selection
        }
      });
    } catch (error) {
      if (onFail) {
        onFail();
      }
      dispatch({ type: types.ADDRESS_VERIFICATION_FAIL, payload: error });
    }
  };
};

export const saveAddress = ({
  addAddressType,
  aviAddress = {},
  aviOptionSelected,
  errorPath,
  onFail,
  onSuccess,
  isSpecialValidation = false //Used to change the endpoint with new validations
}) => {
  const http = getHttpInstance();
  return async (dispatch, getState) => {
    dispatch({ type: types.ADDRESS_SAVE_BEGIN });

    const {
      common: { storeId },
      addresses: {
        addresses: { options, new: newAddress }
      }
    } = getState();
    const { nickName: existingNickname } = newAddress; //logic to verify if the nickName already exists
    const existing =
      existingNickname && options //makes shure that if there are no addresses on the account, the logic doesn't break
        ? options.find(({ nickName }) => nickName === existingNickname)
        : {};
    const isEdit = !isEmpty(existing); //switch to edit mode if the nickName already exists
    const finalAddress = {
      ...existing,
      ...newAddress, //this replaces existing keys ^
      ...aviAddress, //this replaces newAddress keys ^, if it is not empty
      addressType: isEdit ? existing.addressType : addAddressType
    };

    const {
      address1 = '',
      address2 = '',
      addressType,
      email: email1 = '', //the service uses email1 key
      phone: phone1 = '', //the service uses phone1 key
      aviSelection,
      nickName,
      firstName,
      lastName,
      country,
      zipCode,
      city,
      state
    } = finalAddress;

    const endpointBase = replaceTokensInUrl(
      isSpecialValidation
        ? endpoints.ADDRESS_SAVE_WITH_VALIDATIONS
        : endpoints.ADDRESS_SAVE,
      storeId
    );

    const endpointURL = isEdit //endpoint changes if it is an edit
      ? `${endpointBase}/${encodeURIComponent(finalAddress.nickName)}`
      : endpointBase;
    const method = isEdit ? 'put' : 'post';
    try {
      const {
        data: { addressId }
      } = await http.request({
        url: endpointURL,
        method,
        data: {
          aviSelection,
          nickName,
          firstName,
          lastName,
          country,
          zipCode,
          city,
          state,
          addressType,
          email1,
          phone1,
          addressLine: [address1, address2]
        }
      });
      const addressTypeMap = {
        SB: types.SB,
        B: types.B,
        S: types.S
      };
      const normalizedAddressType = addressTypeMap[addressType] || addressType;
      const savedAddress = {
        ...finalAddress,
        addressType: normalizedAddressType,
        addressLine: [address1, address2],
        email1,
        phone1,
        addressId
      };
      dispatch({
        type: types.ADDRESS_SAVE_SUCCESS,
        payload: {
          ...finalAddress,
          addressType: normalizedAddressType,
          addressLine: [address1, address2],
          email1,
          phone1,
          addressId
          // userId // not shure if this is used at all
        }
      });
      if (onSuccess) {
        onSuccess(savedAddress, isEdit, aviOptionSelected);
      }
    } catch (payload) {
      const error = normalizeError(payload);
      if (onFail) {
        onFail(error, isEdit);
      }
      dispatch({ type: types.ADDRESS_SAVE_FAIL, payload });
      dispatch(setError(ERROR_DOMAIN.ADDRESS_BOOK, errorPath, error));
    }
  };
};

export const deleteAddress =
  (nickName, onSuccess, onFail) => async dispatch => {
    dispatch({ type: types.ADDRESS_DELETE_BEGIN });
    const { storeId } = getStoreInfo();
    const http = getHttpInstance();
    const nameUrl = `${endpoints.ADDRESS_SAVE}/${encodeURIComponent(nickName)}`;
    const url = replaceTokensInUrl(nameUrl, storeId);
    try {
      await http.delete(url);
      dispatch({
        type: types.ADDRESSES_DELETE_SUCCESS,
        payload: nickName
      });
      onSuccess && onSuccess();
    } catch (error) {
      dispatch({
        type: types.ADDRESSES_DELETE_FAIL,
        payload: error
      });
      dispatch(
        setError(
          ERROR_DOMAIN.ADDRESS_BOOK,
          ERROR_PATH.MAIN,
          normalizeError(error)
        )
      );
      onFail && onFail();
    }
  };
